import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { filter, interval, map, of, switchMap, takeWhile, tap } from 'rxjs';
import { UtilsService } from 'src/app/services/utils.service';
import type { Role } from 'src/app/models/role.model';
import {
  ExamInstanceEvaluationStatusEnum,
  ExamInstanceEvaluationStatusEnumLabels,
} from 'src/app/enums/exam-instance-evaluation-status.enum';
import { SkillQuestionTypeEnum } from 'src/app/enums/skill-question-type.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { ExamInstance, ExamInstanceItem } from 'src/app/models/exam_instance.model';
import {
  ExamInstanceEvaluation,
  ExamInstanceEvaluationAnswer,
  ExamInstanceEvaluationAnswerRubricGradingResult,
  ExamInstanceEvaluationBody,
  ExamInstanceEvaluationUpdateBody,
} from 'src/app/models/exam_instance_evaluation.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services';
import { PermissionsService } from 'src/app/services/permissions.service';

interface ExamInstanceItemAnswerFormMeta {
  exam_instance_item: ExamInstanceItem;
  question_type: SkillQuestionTypeEnum;
  options_snapshot: { label: string; is_correct: boolean; sort_order: number }[];
  score_label: string;
  rubric_grading_result: ExamInstanceEvaluationAnswerRubricGradingResult | null;
}

interface EvaluationFormAnswersSnapshot {
  user_id: number | null;
  answers: {
    exam_instance_item_id: number;
    text_answer: string | null;
    selected_option_indexes: number[] | null;
  }[];
}

@Component({
  selector: 'app-exam-instance-evaluation-detail',
  templateUrl: './exam-instance-evaluation-detail.component.html',
  styleUrl: './exam-instance-evaluation-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
})
export class ExamInstanceEvaluationDetailComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snack_bar = inject(MatSnackBar);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly form_builder = inject(FormBuilder);
  private readonly permissions_service = inject(PermissionsService);
  private readonly utils_service = inject(UtilsService);

  readonly SkillQuestionTypeEnum = SkillQuestionTypeEnum;
  readonly ExamInstanceEvaluationStatusEnum = ExamInstanceEvaluationStatusEnum;

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly workflow_loading = signal(false);
  readonly exporting_pdf = signal(false);
  readonly workers = signal<User[]>([]);
  readonly current_evaluation = signal<ExamInstanceEvaluation | null>(null);

  readonly exam_instance_id = toSignal(
    this.activated_route.paramMap.pipe(
      map(param_map => {
        const raw_id = param_map.get('id');
        const parsed_id = raw_id ? Number.parseInt(raw_id, 10) : 0;

        return Number.isNaN(parsed_id) ? 0 : parsed_id;
      })
    ),
    { initialValue: 0 }
  );

  readonly evaluation_id_param = toSignal(this.activated_route.paramMap.pipe(map(param_map => param_map.get('evaluation_id'))), {
    initialValue: null as string | null,
  });

  readonly is_create_mode = computed(() => this.evaluation_id_param() === 'new');

  readonly is_upskilling_context = computed(() => this.router.url.includes('/employees/performance/upskilling/exams'));

  readonly meeting_record_return_url = toSignal(
    this.activated_route.queryParamMap.pipe(map(param_map => param_map.get('meeting_record_return_url'))),
    { initialValue: null as string | null }
  );

  readonly back_route = computed((): string | (string | number)[] => {
    const meeting_record_return_url = this.meeting_record_return_url();

    if (meeting_record_return_url) {
      return meeting_record_return_url;
    }

    return this.is_upskilling_context()
      ? ['/employees', 'performance', 'upskilling', 'exams']
      : ['/employees', 'rrhh', 'exam-instances', this.exam_instance_id()];
  });

  readonly back_label = computed(() => {
    if (this.meeting_record_return_url()) {
      return 'Volver al acta';
    }

    return this.is_upskilling_context() ? 'Volver a mis exámenes' : 'Volver al examen';
  });

  readonly evaluation_id = computed(() => {
    const evaluation_id_param = this.evaluation_id_param();

    if (!evaluation_id_param || evaluation_id_param === 'new') {
      return null;
    }

    const parsed_evaluation_id = Number.parseInt(evaluation_id_param, 10);

    return Number.isNaN(parsed_evaluation_id) ? null : parsed_evaluation_id;
  });

  readonly form = this.form_builder.group({
    user_id: [null as number | null, Validators.required],
    answers: this.form_builder.array([]),
  });

  readonly status_label = computed(() => {
    const evaluation = this.current_evaluation();

    if (!evaluation) {
      return ExamInstanceEvaluationStatusEnumLabels[ExamInstanceEvaluationStatusEnum.Draft];
    }

    return ExamInstanceEvaluationStatusEnumLabels[evaluation.status] ?? evaluation.status;
  });

  readonly global_score_label = computed(() => {
    const score = this.current_evaluation()?.score_over_ten;

    return score !== null && score !== undefined ? `${score} / 10` : '—';
  });

  readonly evaluated_user_label = computed(() => {
    const evaluation = this.current_evaluation();

    if (evaluation === null) {
      return '—';
    }

    const user = evaluation.user;

    if (user != null) {
      return user.full_name?.trim() || this.getWorkerLabel(user);
    }

    return `Usuario #${evaluation.user_id}`;
  });

  readonly is_processing = computed(() => this.current_evaluation()?.status === ExamInstanceEvaluationStatusEnum.Processing);

  readonly can_edit_answers = computed(() => {
    if (this.is_create_mode()) {
      return true;
    }

    const status = this.current_evaluation()?.status;

    return status === ExamInstanceEvaluationStatusEnum.Draft || status === ExamInstanceEvaluationStatusEnum.NeedsReview;
  });

  readonly show_workflow_actions = computed(() => !this.is_create_mode() && this.evaluation_id() !== null);

  private readonly saved_form_snapshot = signal<EvaluationFormAnswersSnapshot | null>(null);

  private readonly form_change_tick = signal(0);

  readonly form_has_unsaved_changes = computed(() => {
    this.form_change_tick();

    const snapshot = this.saved_form_snapshot();

    if (snapshot === null) {
      return false;
    }

    return !this.isFormEqualToSnapshot(snapshot);
  });

  readonly show_save_button = computed(() => {
    this.form_change_tick();

    if (!this.can_edit_answers() || this.is_processing()) {
      return false;
    }

    return this.form_has_unsaved_changes();
  });

  readonly has_imported_scanned_pdf = computed(() => Boolean(this.current_evaluation()?.imported_pdf_storage_path));

  /** @deprecated Usar has_imported_scanned_pdf() */
  readonly can_export_answered_pdf = computed(() => this.has_imported_scanned_pdf());

  readonly can_import_pdf = computed(() => {
    if (!this.show_workflow_actions() || this.is_processing() || this.workflow_loading()) {
      return false;
    }

    const status = this.current_evaluation()?.status;

    return status === ExamInstanceEvaluationStatusEnum.Draft || status === ExamInstanceEvaluationStatusEnum.NeedsReview;
  });

  readonly can_show_rubric_grading = computed(() => {
    const status = this.current_evaluation()?.status;

    return status === ExamInstanceEvaluationStatusEnum.Completed || status === ExamInstanceEvaluationStatusEnum.NeedsReview;
  });

  readonly can_show_correct_button = computed(() => {
    this.form_change_tick();

    if (!this.show_workflow_actions() || !this.can_edit_answers()) {
      return false;
    }

    if (this.is_processing() || this.workflow_loading()) {
      return false;
    }

    if (this.form_has_unsaved_changes()) {
      return false;
    }

    return this.areAllAnswersSavedAndCompleteInBackend();
  });

  readonly exam_instance = toSignal(
    toObservable(this.exam_instance_id).pipe(
      switchMap(exam_instance_id => {
        if (!exam_instance_id) {
          this.loading.set(false);
          return of(null);
        }

        this.loading.set(true);

        return this.api_service.getExamInstance(exam_instance_id, { with: 'exam_instance_items' }).pipe(tap(() => this.loading.set(false)));
      })
    ),
    { initialValue: null as ExamInstance | null }
  );

  readonly answer_items_meta = signal<ExamInstanceItemAnswerFormMeta[]>([]);

  constructor() {
    this.listenWorkers();

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(() => {
      this.form_change_tick.update(tick => tick + 1);
    });

    toObservable(this.exam_instance)
      .pipe(
        filter((exam_instance): exam_instance is ExamInstance => exam_instance !== null),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(exam_instance => {
        if (this.is_create_mode()) {
          this.buildAnswersForm(exam_instance, []);
          return;
        }

        const evaluation_id = this.evaluation_id();

        if (!evaluation_id) {
          return;
        }

        this.api_service
          .getExamInstanceEvaluation(evaluation_id, {
            with: 'exam_instance_evaluation_answers,exam_instance.exam_instance_items,user',
          })
          .pipe(takeUntilDestroyed(this.destroy_ref))
          .subscribe({
            next: (evaluation: ExamInstanceEvaluation): void => {
              this.applyEvaluation(evaluation, evaluation.exam_instance ?? exam_instance);
            },
            error: (): void => {
              this.snack_bar.open('No se pudo cargar la evaluación.', undefined, { duration: 5000 });
              void this.router.navigate(['/employees', 'rrhh', 'exam-instances', this.exam_instance_id()]);
            },
          });
      });
  }

  get answers_form_array(): FormArray {
    return this.form.controls.answers;
  }

  toggleMultipleOption(answer_index: number, option_index: number, checked: boolean): void {
    const answer_control = this.answers_form_array.at(answer_index);
    const current_indexes = this.getSelectedOptionIndexes(answer_index);
    let next_indexes: number[];

    if (checked) {
      next_indexes = [...current_indexes, option_index];
    } else {
      next_indexes = current_indexes.filter(index => index !== option_index);
    }

    answer_control.patchValue({ selected_option_indexes: next_indexes });
  }

  setSingleOption(answer_index: number, option_index: number): void {
    this.answers_form_array.at(answer_index).patchValue({ selected_option_indexes: [option_index] });
  }

  save(): void {
    if (this.saving() || this.loading() || this.form.invalid) {
      return;
    }

    const exam_instance_id = this.exam_instance_id();

    if (!exam_instance_id) {
      return;
    }

    if (!this.can_edit_answers()) {
      return;
    }

    const raw = this.form.getRawValue();

    this.saving.set(true);

    const answers_payload = this.answers_form_array.controls.map(answer_control => {
      const answer_value = answer_control.getRawValue() as {
        exam_instance_item_id: number;
        text_answer: string | null;
        selected_option_indexes: number[] | null;
      };

      return {
        exam_instance_item_id: answer_value.exam_instance_item_id,
        text_answer: answer_value.text_answer?.trim() ? answer_value.text_answer.trim() : null,
        selected_option_indexes:
          answer_value.selected_option_indexes && answer_value.selected_option_indexes.length > 0
            ? answer_value.selected_option_indexes
            : null,
      };
    });

    if (this.is_create_mode()) {
      const body: ExamInstanceEvaluationBody = {
        user_id: raw.user_id as number,
        exam_instance_id,
        answers: answers_payload,
      };

      this.api_service
        .createExamInstanceEvaluation(body)
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe({
          next: (): void => {
            this.snack_bar.open('Evaluación creada', undefined, { duration: 3000 });
            void this.router.navigate(['/employees', 'rrhh', 'exam-instances', exam_instance_id]);
          },
          error: (err: { error?: { error?: string } }): void => {
            this.saving.set(false);
            const message = err?.error?.error ?? 'Error al guardar.';
            this.snack_bar.open(message, undefined, { duration: 5000 });
          },
        });

      return;
    }

    const evaluation_id = this.evaluation_id();

    if (!evaluation_id) {
      this.saving.set(false);
      return;
    }

    const update_body: ExamInstanceEvaluationUpdateBody = {
      answers: answers_payload,
    };

    this.api_service
      .updateExamInstanceEvaluation(evaluation_id, update_body)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (evaluation: ExamInstanceEvaluation): void => {
          this.snack_bar.open('Evaluación guardada', undefined, { duration: 3000 });
          this.applyEvaluation(evaluation, this.exam_instance() ?? evaluation.exam_instance!);
          this.saving.set(false);
        },
        error: (err: { error?: { error?: string } }): void => {
          this.saving.set(false);
          const message = err?.error?.error ?? 'Error al guardar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  getWorkerLabel(user: User): string {
    return `${user.name} ${user.surnames}`.trim();
  }

  private getSelectedOptionIndexes(answer_index: number): number[] {
    const answer_control = this.answers_form_array.at(answer_index);
    const selected_option_indexes = answer_control.get('selected_option_indexes')?.value;

    return Array.isArray(selected_option_indexes) ? selected_option_indexes : [];
  }

  private buildAnswersForm(exam_instance: ExamInstance, existing_answers: ExamInstanceEvaluationAnswer[]): void {
    this.answers_form_array.clear();

    const answers_by_item_id = new Map(existing_answers.map(existing_answer => [existing_answer.exam_instance_item_id, existing_answer]));

    const sorted_items = [...(exam_instance.exam_instance_items ?? [])].sort(
      (first_item, second_item) => first_item.sort_order - second_item.sort_order
    );

    const answer_items_meta: ExamInstanceItemAnswerFormMeta[] = [];

    sorted_items.forEach(exam_instance_item => {
      const existing_answer = answers_by_item_id.get(exam_instance_item.id);
      const options_snapshot = [...(exam_instance_item.options_snapshot ?? [])].sort(
        (first_option, second_option) => first_option.sort_order - second_option.sort_order
      );

      const answer_score = existing_answer?.score;

      answer_items_meta.push({
        exam_instance_item,
        question_type: exam_instance_item.question_type,
        options_snapshot,
        score_label: answer_score !== null && answer_score !== undefined ? `${answer_score} / 10` : '—',
        rubric_grading_result: existing_answer?.rubric_grading_result ?? null,
      });

      this.answers_form_array.push(
        this.form_builder.group({
          exam_instance_item_id: [exam_instance_item.id, Validators.required],
          text_answer: [existing_answer?.text_answer ?? ''],
          selected_option_indexes: [existing_answer?.selected_option_indexes ?? ([] as number[])],
        })
      );
    });

    this.answer_items_meta.set(answer_items_meta);
    this.captureFormSnapshot();
  }

  startCorrection(): void {
    const evaluation_id = this.evaluation_id();

    if (!evaluation_id || this.workflow_loading() || this.is_processing()) {
      return;
    }

    if (!this.can_show_correct_button()) {
      const message = this.form_has_unsaved_changes()
        ? 'Guarda la evaluación antes de corregir.'
        : 'Responde todas las preguntas y guarda la evaluación antes de corregir.';

      this.snack_bar.open(message, undefined, { duration: 5000 });

      return;
    }

    this.workflow_loading.set(true);

    this.api_service
      .correctExamInstanceEvaluation(evaluation_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (evaluation: ExamInstanceEvaluation): void => {
          this.workflow_loading.set(false);
          this.snack_bar.open('Corrección iniciada', undefined, { duration: 3000 });
          this.applyEvaluation(evaluation, this.exam_instance() ?? evaluation.exam_instance!);
          this.startStatusPolling(evaluation_id);
        },
        error: (err: { error?: { error?: string } }): void => {
          this.workflow_loading.set(false);
          this.snack_bar.open(err?.error?.error ?? 'No se pudo iniciar la corrección.', undefined, {
            duration: 5000,
          });
        },
      });
  }

  onImportPdfSelected(event: Event): void {
    const evaluation_id = this.evaluation_id();
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = '';

    if (!evaluation_id || !file || this.workflow_loading() || !this.can_import_pdf()) {
      return;
    }

    this.workflow_loading.set(true);

    this.api_service
      .importExamInstanceEvaluationPdf(evaluation_id, file)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (evaluation: ExamInstanceEvaluation): void => {
          this.workflow_loading.set(false);
          this.snack_bar.open('Importación iniciada', undefined, { duration: 3000 });
          this.applyEvaluation(evaluation, this.exam_instance() ?? evaluation.exam_instance!);
          this.startStatusPolling(evaluation_id);
        },
        error: (err: { error?: { error?: string } }): void => {
          this.workflow_loading.set(false);
          this.snack_bar.open(err?.error?.error ?? 'No se pudo importar el PDF.', undefined, { duration: 5000 });
        },
      });
  }

  exportPdf(): void {
    const evaluation_id = this.evaluation_id();

    if (!evaluation_id || this.exporting_pdf()) {
      return;
    }

    this.exporting_pdf.set(true);

    this.api_service
      .exportExamInstanceEvaluationPdf(evaluation_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response): void => {
          this.exporting_pdf.set(false);

          const filename =
            response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') ?? `evaluacion_${evaluation_id}.pdf`;

          this.utils_service.downloadDocumentFromBlob(response.body as Blob, filename);
        },
        error: async (error: unknown): Promise<void> => {
          this.exporting_pdf.set(false);
          const message = (await this.utils_service.parseApiErrorFromBlob(error)) ?? 'No se pudo exportar el PDF.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  /** @deprecated Usar downloadImportedScannedPdf() */
  exportAnsweredPdf(): void {
    this.downloadImportedScannedPdf();
  }

  downloadImportedScannedPdf(): void {
    const evaluation_id = this.evaluation_id();

    if (!evaluation_id || this.exporting_pdf() || !this.has_imported_scanned_pdf()) {
      return;
    }

    this.exporting_pdf.set(true);

    this.api_service
      .downloadExamInstanceEvaluationImportedPdf(evaluation_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response): void => {
          this.exporting_pdf.set(false);

          const filename =
            response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') ??
            `examen_escaneado_${evaluation_id}.pdf`;

          this.utils_service.downloadDocumentFromBlob(response.body as Blob, filename);
        },
        error: (): void => {
          this.exporting_pdf.set(false);
          this.snack_bar.open('No se pudo descargar el examen escaneado.', undefined, { duration: 5000 });
        },
      });
  }

  private applyEvaluation(evaluation: ExamInstanceEvaluation, exam_instance: ExamInstance): void {
    this.current_evaluation.set(evaluation);

    this.form.patchValue({
      user_id: evaluation.user_id,
    });

    this.form.controls.user_id.disable();

    const existing_answers = evaluation.exam_instance_evaluation_answers ?? [];

    this.buildAnswersForm(exam_instance, existing_answers);

    if (!this.can_edit_answers()) {
      this.form.disable();
    } else {
      this.form.enable();
      this.form.controls.user_id.disable();
    }
  }

  private startStatusPolling(evaluation_id: number): void {
    interval(4000)
      .pipe(
        switchMap(() =>
          this.api_service.getExamInstanceEvaluation(evaluation_id, {
            with: 'exam_instance_evaluation_answers.exam_instance_item,exam_instance.exam_instance_items,user',
          })
        ),
        takeWhile(evaluation => evaluation.status === ExamInstanceEvaluationStatusEnum.Processing, true),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe({
        next: (evaluation: ExamInstanceEvaluation): void => {
          this.applyEvaluation(evaluation, evaluation.exam_instance ?? this.exam_instance()!);

          if (evaluation.status !== ExamInstanceEvaluationStatusEnum.Processing) {
            this.snack_bar.open('Proceso finalizado', undefined, { duration: 3000 });
          }
        },
      });
  }

  private captureFormSnapshot(): void {
    this.saved_form_snapshot.set(this.buildSnapshotFromForm());
    this.form_change_tick.update(tick => tick + 1);
  }

  private buildSnapshotFromForm(): EvaluationFormAnswersSnapshot {
    const raw = this.form.getRawValue();

    return {
      user_id: raw.user_id,
      answers: this.answers_form_array.controls.map(answer_control => {
        const answer_value = answer_control.getRawValue() as {
          exam_instance_item_id: number;
          text_answer: string | null;
          selected_option_indexes: number[] | null;
        };

        const selected_option_indexes =
          answer_value.selected_option_indexes && answer_value.selected_option_indexes.length > 0
            ? [...answer_value.selected_option_indexes].sort((first_index, second_index) => first_index - second_index)
            : null;

        return {
          exam_instance_item_id: answer_value.exam_instance_item_id,
          text_answer: answer_value.text_answer?.trim() ? answer_value.text_answer.trim() : null,
          selected_option_indexes,
        };
      }),
    };
  }

  private isFormEqualToSnapshot(snapshot: EvaluationFormAnswersSnapshot): boolean {
    return JSON.stringify(this.buildSnapshotFromForm()) === JSON.stringify(snapshot);
  }

  private areAllAnswersSavedAndCompleteInBackend(): boolean {
    const evaluation = this.current_evaluation();
    const exam_instance = this.exam_instance();

    if (!evaluation || !exam_instance) {
      return false;
    }

    const exam_instance_items = exam_instance.exam_instance_items ?? [];

    if (exam_instance_items.length === 0) {
      return false;
    }

    const answers_by_exam_instance_item_id = new Map(
      (evaluation.exam_instance_evaluation_answers ?? []).map(saved_answer => [saved_answer.exam_instance_item_id, saved_answer])
    );

    return exam_instance_items.every(exam_instance_item => {
      const saved_answer = answers_by_exam_instance_item_id.get(exam_instance_item.id);

      if (!saved_answer) {
        return false;
      }

      return this.isSavedAnswerComplete(exam_instance_item.question_type, saved_answer);
    });
  }

  private isSavedAnswerComplete(question_type: SkillQuestionTypeEnum, saved_answer: ExamInstanceEvaluationAnswer): boolean {
    const has_text_answer = Boolean(saved_answer.text_answer?.trim());
    const has_selected_options = Array.isArray(saved_answer.selected_option_indexes) && saved_answer.selected_option_indexes.length > 0;

    if (question_type === SkillQuestionTypeEnum.Text) {
      return has_text_answer;
    }

    return has_selected_options;
  }

  private listenWorkers(): void {
    this.permissions_service.rolesObservable
      .pipe(
        filter((roles): roles is Record<string, Role> => roles !== null),
        map(roles => Object.values(roles).find(role => role.slug === 'worker')),
        filter((worker_role): worker_role is Role => worker_role !== undefined),
        switchMap(worker_role =>
          this.api_service.getUsers({
            role_id: worker_role.id,
            scopes: 'activeWorkers',
            per_page: 500,
            order_by: 'name:asc',
          })
        ),
        map((response: ArrayResponse<User> | User[]) => (Array.isArray(response) ? response : response.data)),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(workers => this.workers.set(workers));
  }
}
